const COLOR_FILTERS = {
  SATURATED: "saturated",
  BRIGHT: "bright",
  VIBRANT: "vibrant",
  RETRO: "retro",
  BLACK_WHITE: "blackWhite",
  SEPIA: "sepia",
  COOL: "cool",
  WARM: "warm",
  CINEMATIC: "cinematic",
  SOFT_GLOW: "softGlow",
  MOODY: "moody",
  DREAMY: "dreamy",
  INVERTED: "inverted",
  VINTAGE: "vintage",
  DRAMATIC: "dramatic",
  FADED: "faded"
};

const imageDimensionsCache = {};
const videoMetaCache = {};
const audioDurationCache = {};

const getAudioDuration = (audioSrc) => {
  if (audioDurationCache[audioSrc]) {
    return Promise.resolve(audioDurationCache[audioSrc]);
  }
  return new Promise((resolve, reject) => {
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    const isSafeUrl = /^(https?:|blob:|data:audio\/)/i.test(audioSrc);
    if (!isSafeUrl) {
      throw new Error("Unsafe audio source URL");
    }
    audio.src = audioSrc;
    audio.onloadedmetadata = () => {
      const duration = audio.duration;
      audioDurationCache[audioSrc] = duration;
      resolve(duration);
    };
    audio.onerror = () => {
      reject(new Error("Failed to load audio metadata"));
    };
  });
};

const concurrencyLimit = 5;
let activeCount = 0;
const queue = [];
function runNext() {
  if (queue.length === 0 || activeCount >= concurrencyLimit) return;
  const next = queue.shift();
  if (next) {
    activeCount++;
    next();
  }
}
function limit(fn) {
  return new Promise((resolve, reject) => {
    const task = () => {
      fn().then(resolve).catch(reject).finally(() => {
        activeCount--;
        runNext();
      });
    };
    if (activeCount < concurrencyLimit) {
      activeCount++;
      task();
    } else {
      queue.push(task);
    }
  });
}

const loadImageDimensions = (url) => {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("getImageDimensions() is only available in the browser."));
      return;
    }
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = url;
  });
};
const getImageDimensions = (url) => {
  if (imageDimensionsCache[url]) {
    return Promise.resolve(imageDimensionsCache[url]);
  }
  return limit(() => loadImageDimensions(url)).then((dimensions) => {
    imageDimensionsCache[url] = dimensions;
    return dimensions;
  });
};

const getVideoMeta = (videoSrc) => {
  if (videoMetaCache[videoSrc]) {
    return Promise.resolve(videoMetaCache[videoSrc]);
  }
  return new Promise((resolve, reject) => {
    const isSafeUrl = /^(https?:|blob:|data:video\/)/i.test(videoSrc);
    if (!isSafeUrl) {
      reject(new Error("Unsafe video source URL"));
      return;
    }
    const tryLoadVideo = (useCors) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.crossOrigin = useCors ? "anonymous" : null;
      video.src = videoSrc;
      video.onloadedmetadata = () => {
        const meta = {
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration
        };
        videoMetaCache[videoSrc] = meta;
        resolve(meta);
      };
      video.onerror = () => {
        if (useCors) {
          video.src = "";
          tryLoadVideo(false);
        } else {
          reject(new Error("Failed to load video metadata. This may be due to CORS restrictions or an invalid video URL."));
        }
      };
    };
    tryLoadVideo(true);
  });
};

const getThumbnail = async (videoUrl, seekTime = 0.1, playbackRate = 1) => {
  return new Promise((resolve, reject) => {
    const tryLoadThumbnail = (useCors) => {
      const video = document.createElement("video");
      video.crossOrigin = useCors ? "anonymous" : null;
      video.muted = true;
      video.playsInline = true;
      video.autoplay = false;
      video.preload = "auto";
      video.playbackRate = playbackRate;
      video.style.position = "absolute";
      video.style.left = "-9999px";
      video.style.top = "-9999px";
      video.style.width = "1px";
      video.style.height = "1px";
      video.style.opacity = "0";
      video.style.pointerEvents = "none";
      video.style.zIndex = "-1";
      let timeoutId;
      const cleanup = () => {
        if (video.parentNode) video.remove();
        if (timeoutId) clearTimeout(timeoutId);
      };
      const handleError = () => {
        cleanup();
        if (useCors) {
          tryLoadThumbnail(false);
        } else {
          reject(new Error(`Failed to load video: ${video.error?.message || "Unknown error. This may be due to CORS restrictions or an invalid video URL."}`));
        }
      };
      const handleSeeked = () => {
        try {
          video.pause();
          const canvas = document.createElement("canvas");
          const width = video.videoWidth || 640;
          const height = video.videoHeight || 360;
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            cleanup();
            reject(new Error("Failed to get canvas context"));
            return;
          }
          ctx.drawImage(video, 0, 0, width, height);
          try {
            const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
            cleanup();
            resolve(dataUrl);
          } catch {
            canvas.toBlob((blob) => {
              if (!blob) {
                cleanup();
                reject(new Error("Failed to create Blob"));
                return;
              }
              const blobUrl = URL.createObjectURL(blob);
              cleanup();
              resolve(blobUrl);
            }, "image/jpeg", 0.8);
          }
        } catch (err) {
          cleanup();
          reject(new Error(`Error creating thumbnail: ${err}`));
        }
      };
      video.addEventListener("error", handleError, { once: true });
      video.addEventListener("seeked", handleSeeked, { once: true });
      video.addEventListener("loadedmetadata", () => {
        const playPromise = video.play();
        if (playPromise !== void 0) {
          playPromise.then(() => {
            video.currentTime = seekTime || 0.1;
          }).catch(() => {
            video.currentTime = seekTime || 0.1;
          });
        } else {
          video.currentTime = seekTime || 0.1;
        }
      }, { once: true });
      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error("Video loading timed out"));
      }, 15e3);
      video.src = videoUrl;
      document.body.appendChild(video);
    };
    tryLoadThumbnail(true);
  });
};

class LRUCache {
  constructor(maxSize = 100) {
    if (maxSize <= 0) {
      throw new Error("maxSize must be greater than 0");
    }
    this.maxSize = maxSize;
    this.cache = /* @__PURE__ */ new Map();
  }
  /**
   * Get a value from the cache.
   * Moves the item to the end (most recently used).
   */
  get(key) {
    const value = this.cache.get(key);
    if (value === void 0) {
      return void 0;
    }
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }
  /**
   * Set a value in the cache.
   * If cache is full, removes the least recently used item.
   */
  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== void 0) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }
  /**
   * Check if a key exists in the cache.
   */
  has(key) {
    return this.cache.has(key);
  }
  /**
   * Delete a key from the cache.
   */
  delete(key) {
    return this.cache.delete(key);
  }
  /**
   * Clear all entries from the cache.
   */
  clear() {
    this.cache.clear();
  }
  /**
   * Get the current size of the cache.
   */
  get size() {
    return this.cache.size;
  }
}

class VideoFrameExtractor {
  constructor(options = {}) {
    this.frameCache = new LRUCache(
      options.maxCacheSize ?? 50
    );
    this.videoElements = /* @__PURE__ */ new Map();
    this.maxVideoElements = options.maxVideoElements ?? 5;
    this.loadTimeout = options.loadTimeout ?? 15e3;
    this.jpegQuality = options.jpegQuality ?? 0.8;
    this.playbackRate = options.playbackRate ?? 1;
  }
  /**
   * Get a frame thumbnail from a video at a specific time.
   * Uses caching and reuses video elements for optimal performance.
   * Uses 0.1s instead of 0 when seekTime is 0, since frames at t=0 are often blank.
   *
   * @param videoUrl - The URL of the video
   * @param seekTime - The time in seconds to extract the frame (0 is treated as 0.1)
   * @returns Promise resolving to a thumbnail image URL (data URL or blob URL)
   */
  async getFrame(videoUrl, seekTime = 0.1) {
    const effectiveSeekTime = seekTime === 0 ? 0.1 : seekTime;
    const cacheKey = this.getCacheKey(videoUrl, effectiveSeekTime);
    const cached = this.frameCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const videoState = await this.getVideoElement(videoUrl);
    const thumbnail = await this.extractFrame(videoState.video, effectiveSeekTime);
    this.frameCache.set(cacheKey, thumbnail);
    return thumbnail;
  }
  /**
   * Get or create a video element for the given URL.
   * Reuses existing elements and manages cleanup.
   */
  async getVideoElement(videoUrl) {
    let videoState = this.videoElements.get(videoUrl);
    if (videoState && videoState.isReady) {
      videoState.lastUsed = Date.now();
      return videoState;
    }
    if (videoState && videoState.isLoading && videoState.loadPromise) {
      await videoState.loadPromise;
      if (videoState.isReady) {
        videoState.lastUsed = Date.now();
        return videoState;
      }
    }
    if (this.videoElements.size >= this.maxVideoElements) {
      this.cleanupOldVideoElements();
    }
    videoState = await this.createVideoElement(videoUrl);
    this.videoElements.set(videoUrl, videoState);
    videoState.lastUsed = Date.now();
    return videoState;
  }
  /**
   * Create and initialize a new video element.
   */
  async createVideoElement(videoUrl) {
    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.muted = true;
    video.playsInline = true;
    video.autoplay = false;
    video.preload = "auto";
    video.playbackRate = this.playbackRate;
    video.style.position = "absolute";
    video.style.left = "-9999px";
    video.style.top = "-9999px";
    video.style.width = "1px";
    video.style.height = "1px";
    video.style.opacity = "0";
    video.style.pointerEvents = "none";
    video.style.zIndex = "-1";
    const state = {
      video,
      isReady: false,
      isLoading: true,
      loadPromise: null,
      lastUsed: Date.now()
    };
    state.loadPromise = new Promise((resolve, reject) => {
      let timeoutId;
      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
      const handleError = () => {
        cleanup();
        state.isLoading = false;
        reject(new Error(`Failed to load video: ${video.error?.message || "Unknown error"}`));
      };
      const handleLoadedMetadata = () => {
        cleanup();
        state.isReady = true;
        state.isLoading = false;
        resolve();
      };
      video.addEventListener("error", handleError, { once: true });
      video.addEventListener("loadedmetadata", handleLoadedMetadata, { once: true });
      timeoutId = window.setTimeout(() => {
        cleanup();
        state.isLoading = false;
        reject(new Error("Video loading timed out"));
      }, this.loadTimeout);
      video.src = videoUrl;
      document.body.appendChild(video);
    });
    try {
      await state.loadPromise;
    } catch (error) {
      if (video.parentNode) {
        video.remove();
      }
      throw error;
    }
    return state;
  }
  /**
   * Extract a frame from a video at the specified time.
   */
  async extractFrame(video, seekTime) {
    return new Promise((resolve, reject) => {
      video.pause();
      const timeThreshold = 0.1;
      if (Math.abs(video.currentTime - seekTime) < timeThreshold) {
        try {
          const canvas = document.createElement("canvas");
          const width = video.videoWidth || 640;
          const height = video.videoHeight || 360;
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Failed to get canvas context"));
            return;
          }
          ctx.drawImage(video, 0, 0, width, height);
          try {
            const dataUrl = canvas.toDataURL("image/jpeg", this.jpegQuality);
            resolve(dataUrl);
          } catch {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error("Failed to create Blob"));
                  return;
                }
                const blobUrl = URL.createObjectURL(blob);
                resolve(blobUrl);
              },
              "image/jpeg",
              this.jpegQuality
            );
          }
          return;
        } catch (err) {
          reject(new Error(`Error creating thumbnail: ${err}`));
          return;
        }
      }
      const handleSeeked = () => {
        try {
          const canvas = document.createElement("canvas");
          const width = video.videoWidth || 640;
          const height = video.videoHeight || 360;
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Failed to get canvas context"));
            return;
          }
          ctx.drawImage(video, 0, 0, width, height);
          try {
            const dataUrl = canvas.toDataURL("image/jpeg", this.jpegQuality);
            resolve(dataUrl);
          } catch {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error("Failed to create Blob"));
                  return;
                }
                const blobUrl = URL.createObjectURL(blob);
                resolve(blobUrl);
              },
              "image/jpeg",
              this.jpegQuality
            );
          }
        } catch (err) {
          reject(new Error(`Error creating thumbnail: ${err}`));
        }
      };
      video.addEventListener("seeked", handleSeeked, { once: true });
      const playPromise = video.play();
      if (playPromise !== void 0) {
        playPromise.then(() => {
          video.currentTime = seekTime;
        }).catch(() => {
          video.currentTime = seekTime;
        });
      } else {
        video.currentTime = seekTime;
      }
    });
  }
  /**
   * Generate cache key for a video URL and seek time.
   */
  getCacheKey(videoUrl, seekTime) {
    const roundedTime = Math.round(seekTime * 100) / 100;
    return `${videoUrl}:${roundedTime}`;
  }
  /**
   * Cleanup least recently used video elements.
   */
  cleanupOldVideoElements() {
    if (this.videoElements.size < this.maxVideoElements) {
      return;
    }
    const entries = Array.from(this.videoElements.entries());
    entries.sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    const toRemove = entries.slice(0, entries.length - this.maxVideoElements + 1);
    for (const [url, state] of toRemove) {
      if (state.video.parentNode) {
        state.video.remove();
      }
      this.videoElements.delete(url);
    }
  }
  /**
   * Clear the frame cache.
   */
  clearCache() {
    this.frameCache.clear();
  }
  /**
   * Remove a specific video element and clear its cached frames.
   */
  removeVideo(videoUrl) {
    const state = this.videoElements.get(videoUrl);
    if (state) {
      if (state.video.parentNode) {
        state.video.remove();
      }
      this.videoElements.delete(videoUrl);
    }
    this.frameCache.clear();
  }
  /**
   * Dispose of all video elements and clear caches.
   * Removes all video elements from the DOM and clears both the frame cache
   * and video element cache. Call this when the extractor is no longer needed
   * to prevent memory leaks.
   */
  dispose() {
    for (const state of this.videoElements.values()) {
      if (state.video.parentNode) {
        state.video.remove();
      }
    }
    this.videoElements.clear();
    this.frameCache.clear();
  }
}
let defaultExtractor = null;
function getDefaultVideoFrameExtractor(options) {
  if (!defaultExtractor) {
    defaultExtractor = new VideoFrameExtractor(options);
  }
  return defaultExtractor;
}
async function getThumbnailCached(videoUrl, seekTime = 0.1, playbackRate) {
  const extractor = getDefaultVideoFrameExtractor(
    playbackRate !== void 0 ? { playbackRate } : void 0
  );
  return extractor.getFrame(videoUrl, seekTime);
}

const extractAudio = async ({
  src,
  playbackRate = 1,
  start = 0,
  end
}) => {
  if (!src) throw new Error("src is required");
  if (playbackRate <= 0) throw new Error("playbackRate must be > 0");
  const isSafeUrl = /^(https?:|blob:|data:)/i.test(src);
  if (!isSafeUrl) throw new Error("Unsafe media source URL");
  const audioBuffer = await fetchAndDecodeAudio(src);
  if (audioBuffer.duration === 0 || audioBuffer.length === 0) {
    throw new Error("No audio track found in the media source");
  }
  if (isAudioSilent(audioBuffer)) {
    throw new Error("Audio track is silent (no audio content detected)");
  }
  const clampedStart = Math.max(0, start || 0);
  const fullDuration = audioBuffer.duration;
  const clampedEnd = Math.min(
    typeof end === "number" ? end : fullDuration,
    fullDuration
  );
  if (clampedEnd <= clampedStart)
    throw new Error("Invalid range: end must be greater than start");
  const renderedBuffer = await renderAudioSegment(
    audioBuffer,
    clampedStart,
    clampedEnd,
    playbackRate
  );
  const mp3Blob = await audioBufferToMp3(renderedBuffer);
  return URL.createObjectURL(mp3Blob);
};
const hasAudio = async (src) => {
  if (!src) return false;
  const isSafeUrl = /^(https?:|blob:|data:)/i.test(src);
  if (!isSafeUrl) return false;
  try {
    const audioBuffer = await fetchAndDecodeAudio(src);
    if (audioBuffer.duration === 0 || audioBuffer.length === 0) {
      return false;
    }
    if (isAudioSilent(audioBuffer)) {
      return false;
    }
    return true;
  } catch (error) {
    return false;
  }
};
const stitchAudio = async (segments, totalDuration) => {
  if (!segments || segments.length === 0) {
    throw new Error("At least one audio segment is required");
  }
  const duration = totalDuration || Math.max(...segments.map((s) => s.e));
  const renderedBuffer = await createAudioTimeline(segments, duration);
  const mp3Blob = await audioBufferToMp3(renderedBuffer);
  return URL.createObjectURL(mp3Blob);
};
const fetchAndDecodeAudio = async (src) => {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`Failed to fetch source: ${response.status}`);
  const arrayBuffer = await response.arrayBuffer();
  return decodeAudioData(arrayBuffer);
};
const decodeAudioData = async (arrayBuffer) => {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) throw new Error("Web Audio API not supported");
  const audioContext = new AudioContextCtor();
  try {
    return await new Promise((resolve, reject) => {
      audioContext.decodeAudioData(
        arrayBuffer.slice(0),
        (buf) => resolve(buf),
        (err) => reject(err || new Error("Failed to decode audio: no audio track found or unsupported format"))
      );
    });
  } finally {
    audioContext.close();
  }
};
const isAudioSilent = (buffer, threshold = 1e-3) => {
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < channelData.length; i += 100) {
      if (Math.abs(channelData[i]) > threshold) {
        return false;
      }
    }
  }
  return true;
};
const renderAudioSegment = async (audioBuffer, start, end, playbackRate) => {
  const OfflineAudioContextCtor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!OfflineAudioContextCtor) throw new Error("OfflineAudioContext not supported");
  const sampleRate = audioBuffer.sampleRate;
  const numChannels = audioBuffer.numberOfChannels;
  const sourceDuration = end - start;
  const renderedFrames = Math.max(
    1,
    Math.ceil(sourceDuration / playbackRate * sampleRate)
  );
  const offline = new OfflineAudioContextCtor(numChannels, renderedFrames, sampleRate);
  const sourceNode = offline.createBufferSource();
  sourceNode.buffer = audioBuffer;
  sourceNode.playbackRate.value = playbackRate;
  sourceNode.connect(offline.destination);
  sourceNode.start(0, start, sourceDuration);
  return await offline.startRendering();
};
const createAudioTimeline = async (segments, duration) => {
  const OfflineAudioContextCtor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!OfflineAudioContextCtor) throw new Error("OfflineAudioContext not supported");
  const sampleRate = 44100;
  const totalFrames = Math.ceil(duration * sampleRate);
  const offline = new OfflineAudioContextCtor(2, totalFrames, sampleRate);
  for (const segment of segments) {
    if (segment.s >= segment.e) continue;
    const volume = segment.volume ?? 1;
    if (volume <= 0) continue;
    try {
      const audioBuffer = await fetchAndDecodeAudio(segment.src);
      const segmentDuration = segment.e - segment.s;
      const sourceDuration = Math.min(segmentDuration, audioBuffer.duration);
      const source = offline.createBufferSource();
      source.buffer = audioBuffer;
      if (volume !== 1) {
        const gainNode = offline.createGain();
        gainNode.gain.value = volume;
        source.connect(gainNode);
        gainNode.connect(offline.destination);
      } else {
        source.connect(offline.destination);
      }
      source.start(segment.s, 0, sourceDuration);
    } catch {
    }
  }
  return await offline.startRendering();
};
const audioBufferToMp3 = async (buffer) => {
  try {
    const wavArrayBuffer = audioBufferToWavArrayBuffer(buffer);
    const pcmBuffer = await decodeAudioData(wavArrayBuffer);
    return await encodePcmToMp3(pcmBuffer);
  } catch (error) {
    return audioBufferToWavBlob(buffer);
  }
};
const audioBufferToWavArrayBuffer = (buffer) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const numFrames = buffer.length;
  const interleaved = interleave(buffer, numChannels, numFrames);
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = interleaved.length * bytesPerSample;
  const bufferSize = 44 + dataSize;
  const arrayBuffer = new ArrayBuffer(bufferSize);
  const view = new DataView(arrayBuffer);
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);
  floatTo16BitPCM(view, 44, interleaved);
  return arrayBuffer;
};
const encodePcmToMp3 = async (buffer) => {
  const lamejs = await import('./index-CXhwwSX-.mjs').then(n => n.i);
  const channels = buffer.numberOfChannels >= 2 ? 2 : 1;
  const targetSampleRate = 22050;
  const downsampledBuffer = downsampleAudioBuffer(buffer, targetSampleRate);
  const kbps = 48;
  const mp3encoder = new lamejs.default.Mp3Encoder(channels, targetSampleRate, kbps);
  const samplesPerFrame = 1152;
  const leftFloat = downsampledBuffer.getChannelData(0);
  const left = floatTo16(leftFloat);
  let right;
  if (channels === 2) {
    const rightFloat = downsampledBuffer.getChannelData(1);
    right = floatTo16(rightFloat);
  }
  const mp3Chunks = [];
  for (let i = 0; i < left.length; i += samplesPerFrame) {
    const leftChunk = left.subarray(i, Math.min(i + samplesPerFrame, left.length));
    let mp3buf;
    if (channels === 2 && right) {
      const rightChunk = right.subarray(i, Math.min(i + samplesPerFrame, right.length));
      mp3buf = mp3encoder.encodeBuffer(leftChunk, rightChunk);
    } else {
      mp3buf = mp3encoder.encodeBuffer(leftChunk);
    }
    if (mp3buf.length > 0) mp3Chunks.push(mp3buf);
  }
  const end = mp3encoder.flush();
  if (end.length > 0) mp3Chunks.push(end);
  return new Blob(mp3Chunks, { type: "audio/mpeg" });
};
const audioBufferToWavBlob = (buffer) => {
  const arrayBuffer = audioBufferToWavArrayBuffer(buffer);
  return new Blob([arrayBuffer], { type: "audio/wav" });
};
const downsampleAudioBuffer = (buffer, targetSampleRate) => {
  if (buffer.sampleRate === targetSampleRate) {
    return buffer;
  }
  const ratio = buffer.sampleRate / targetSampleRate;
  const newLength = Math.round(buffer.length / ratio);
  const newBuffer = new AudioContext().createBuffer(
    buffer.numberOfChannels,
    newLength,
    targetSampleRate
  );
  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const oldData = buffer.getChannelData(channel);
    const newData = newBuffer.getChannelData(channel);
    for (let i = 0; i < newLength; i++) {
      const oldIndex = Math.floor(i * ratio);
      newData[i] = oldData[oldIndex];
    }
  }
  return newBuffer;
};
const interleave = (buffer, numChannels, numFrames) => {
  if (numChannels === 1) {
    return buffer.getChannelData(0).slice(0, numFrames);
  }
  const result = new Float32Array(numFrames * numChannels);
  const channelData = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channelData[ch] = buffer.getChannelData(ch);
  }
  let writeIndex = 0;
  for (let i = 0; i < numFrames; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      result[writeIndex++] = channelData[ch][i];
    }
  }
  return result;
};
const floatTo16BitPCM = (view, offset, input) => {
  let pos = offset;
  for (let i = 0; i < input.length; i++, pos += 2) {
    let s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(pos, s < 0 ? s * 32768 : s * 32767, true);
  }
};
const floatTo16 = (input) => {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output[i] = s < 0 ? s * 32768 : s * 32767;
  }
  return output;
};
const writeString = (view, offset, str) => {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
};

const getScaledDimensions = (width, height, maxWidth, maxHeight) => {
  if (width <= maxWidth && height <= maxHeight) {
    return {
      width: width % 2 === 0 ? width : width - 1,
      height: height % 2 === 0 ? height : height - 1
    };
  }
  const widthRatio = maxWidth / width;
  const heightRatio = maxHeight / height;
  const scale = Math.min(widthRatio, heightRatio);
  let scaledWidth = Math.round(width * scale);
  let scaledHeight = Math.round(height * scale);
  if (scaledWidth % 2 !== 0) {
    scaledWidth -= 1;
  }
  if (scaledHeight % 2 !== 0) {
    scaledHeight -= 1;
  }
  return {
    width: Math.min(scaledWidth, maxWidth),
    height: Math.min(scaledHeight, maxHeight)
  };
};
const getObjectFitSize = (objectFit, elementSize, containerSize) => {
  const elementAspectRatio = elementSize.width / elementSize.height;
  const containerAspectRatio = containerSize.width / containerSize.height;
  switch (objectFit) {
    case "contain":
      if (elementAspectRatio > containerAspectRatio) {
        return {
          width: containerSize.width,
          height: containerSize.width / elementAspectRatio
        };
      } else {
        return {
          width: containerSize.height * elementAspectRatio,
          height: containerSize.height
        };
      }
    case "cover":
      if (elementAspectRatio > containerAspectRatio) {
        return {
          width: containerSize.height * elementAspectRatio,
          height: containerSize.height
        };
      } else {
        return {
          width: containerSize.width,
          height: containerSize.width / elementAspectRatio
        };
      }
    case "fill":
      return {
        width: containerSize.width,
        height: containerSize.height
      };
    default:
      return {
        width: elementSize.width,
        height: elementSize.height
      };
  }
};

const blobUrlToFile = async (blobUrl, fileName) => {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
};
const loadFile = (accept) => {
  return new Promise((resolve, reject) => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = accept;
      input.style.display = "none";
      document.body.appendChild(input);
      const cleanup = () => {
        input.value = "";
        document.body.removeChild(input);
      };
      input.onchange = () => {
        const file = input.files && input.files[0];
        cleanup();
        if (!file) {
          reject(new Error("No file selected"));
          return;
        }
        resolve(file);
      };
      input.click();
    } catch (error) {
      reject(error);
    }
  });
};
const saveAsFile = (content, type, name) => {
  const blob = typeof content === "string" ? new Blob([content], { type }) : content;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};
const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    throw error;
  }
};

const detectMediaTypeFromUrl = async (url) => {
  try {
    const response = await fetch(url, { method: "HEAD" });
    const contentType = response.headers.get("Content-Type");
    if (!contentType) return null;
    if (contentType.startsWith("image/")) return "image";
    if (contentType.startsWith("video/")) return "video";
    if (contentType.startsWith("audio/")) return "audio";
    return null;
  } catch {
    return null;
  }
};

export { COLOR_FILTERS, VideoFrameExtractor, blobUrlToFile, detectMediaTypeFromUrl, downloadFile, extractAudio, getAudioDuration, getDefaultVideoFrameExtractor, getImageDimensions, getObjectFitSize, getScaledDimensions, getThumbnail, getThumbnailCached, getVideoMeta, hasAudio, limit, loadFile, saveAsFile, stitchAudio };
//# sourceMappingURL=index.mjs.map
